import type { Site } from '@/types/site';

interface FacilityDetailTabProps {
  site: Site;
}

export default function FacilityDetailTab({
  site,
}: Readonly<FacilityDetailTabProps>) {
  return (
    <div className="space-y-6">
      {/* 취수 시설 정보 */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          취수 시설 정보
        </h4>
        {site.intakeFacilityInfo && site.intakeFacilityInfo.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {site.intakeFacilityInfo.map((facility, index) => (
              <div key={index} className="bg-white rounded-lg p-4 border">
                <h5 className="font-semibold text-gray-900 mb-3">
                  취수 시설 {index + 1}
                </h5>
                <div className="space-y-2 text-sm">
                  {facility.type && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">시설유형:</span>
                      <span className="text-gray-900">{facility.type}</span>
                    </div>
                  )}
                  {facility.specification && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">시설제원:</span>
                      <span className="text-gray-900">
                        {facility.specification}
                      </span>
                    </div>
                  )}
                  {facility.state && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">시설상태:</span>
                      <span className="text-gray-900">{facility.state}</span>
                    </div>
                  )}
                  {facility.installedDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">설치일자:</span>
                      <span className="text-gray-900">
                        {facility.installedDate}
                      </span>
                    </div>
                  )}
                  {facility.explanatoryNote && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-gray-600 text-xs">비고:</span>
                      <p className="text-gray-900 text-sm mt-1">
                        {facility.explanatoryNote}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <i className="ri-settings-3-line text-4xl text-gray-400 mb-2"></i>
            <p className="text-gray-500">등록된 취수 시설 정보가 없습니다.</p>
          </div>
        )}
      </div>

      {/* 정수 시설 정보 */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          정수 시설 정보
        </h4>
        {site.purifyFacilityInfo && site.purifyFacilityInfo.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {site.purifyFacilityInfo.map((facility, index) => (
              <div key={index} className="bg-white rounded-lg p-4 border">
                <h5 className="font-semibold text-gray-900 mb-3">
                  정수 시설 {index + 1}
                </h5>
                <div className="space-y-2 text-sm">
                  {facility.type && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">시설유형:</span>
                      <span className="text-gray-900">{facility.type}</span>
                    </div>
                  )}
                  {facility.specification && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">시설제원:</span>
                      <span className="text-gray-900">
                        {facility.specification}
                      </span>
                    </div>
                  )}
                  {facility.state && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">시설상태:</span>
                      <span className="text-gray-900">{facility.state}</span>
                    </div>
                  )}
                  {facility.installedDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">설치일자:</span>
                      <span className="text-gray-900">
                        {facility.installedDate}
                      </span>
                    </div>
                  )}
                  {facility.explanatoryNote && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-gray-600 text-xs">비고:</span>
                      <p className="text-gray-900 text-sm mt-1">
                        {facility.explanatoryNote}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <i className="ri-settings-4-line text-4xl text-gray-400 mb-2"></i>
            <p className="text-gray-500">등록된 정수 시설 정보가 없습니다.</p>
          </div>
        )}
      </div>

      {/* 시설 계획 */}
      {site.facilityPlan && (
        <div className="bg-gray-50 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            시설 계획
          </h4>
          <div className="bg-white rounded-lg p-4 border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {site.facilityPlan.repairDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">수리 예정일:</span>
                  <span className="text-gray-900">
                    {site.facilityPlan.repairDate}
                  </span>
                </div>
              )}
              {site.facilityPlan.closeDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">폐쇄 예정일:</span>
                  <span className="text-gray-900">
                    {site.facilityPlan.closeDate}
                  </span>
                </div>
              )}
              {site.facilityPlan.supplyDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">급수 개시일:</span>
                  <span className="text-gray-900">
                    {site.facilityPlan.supplyDate}
                  </span>
                </div>
              )}
              {site.facilityPlan.distanceFromWaterSupply && (
                <div className="flex justify-between">
                  <span className="text-gray-600">배수지와의 거리:</span>
                  <span className="text-gray-900">
                    {site.facilityPlan.distanceFromWaterSupply}m
                  </span>
                </div>
              )}
              {site.facilityPlan.explanatoryNote && (
                <div className="md:col-span-2 pt-3 border-t border-gray-200">
                  <span className="text-gray-600 text-xs">비고:</span>
                  <p className="text-gray-900 text-sm mt-1">
                    {site.facilityPlan.explanatoryNote}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
